/**
 * @file Anything that can run on every page.
 */

import debounce from 'just-debounce-it';
import vHeader from '../components/header/header';
import vElementWrappers from './modules/element-wrappers';

/**
 * Global functions.
 *
 * Runs component js and any global tasks.
 */
const vGlobal = () => {
	vHeader();
	vElementWrappers();

	if (document.querySelector('.v-accordion')) {
		import('./modules/accordion').then(({ default: vAccordion }) => {
			vAccordion();
		});
	}

	if (document.querySelector('.v-menu')) {
		import('../components/menu/menu').then(({ default: vMenu }) => {
			vMenu();
		});
	}

	if (document.querySelector('.v-dropdown-nav')) {
		import('../components/dropdown-nav/dropdown-nav').then(
			({ default: vDropdownNav }) => {
				vDropdownNav();
			},
		);
	}

	if (document.querySelector('.v-hp-hero')) {
		import('../components/hp-hero/hp-hero').then(({ default: vHpHero }) => {
			vHpHero();
		});
	}

	if (document.querySelector('.v-highlights-slider')) {
		import('../components/highlights-slider/highlights-slider').then(
			({ default: vHighlightsSlider }) => {
				vHighlightsSlider();
			},
		);
	}

	if (document.querySelector('.v-featured-quote')) {
		import('../components/featured-quote/featured-quote').then(
			({ default: vFeaturedQuote }) => {
				vFeaturedQuote();
			},
		);
	}

	if (document.querySelector('.v-slides')) {
		import('../components/slides/slides').then(({ default: vSlides }) => {
			vSlides();
		});
	}

	if (document.querySelector('.v-accordion-section')) {
		import('../components/accordion-section/accordion-section').then(
			({ default: vAccordionSection }) => {
				vAccordionSection();
			},
		);
	}

	if (document.querySelector('.v-grantee-grid')) {
		import('../components/grantee-grid/grantee-grid').then(
			({ default: vGranteeGrid }) => {
				vGranteeGrid();
			},
		);
	}

	document.addEventListener('click', (e) => {
		if (e.target.classList.contains('v-facet-pagination__link')) {
			e.preventDefault();
		}
	});

	/**
	 * Get scrollbar width.
	 * @returns {number} Scrollbar width.
	 */
	function getScrollbarWidth() {
		const outer = document.createElement('div');
		outer.style.visibility = 'hidden';
		outer.style.overflow = 'scroll';
		document.body.appendChild(outer);

		const inner = document.createElement('div');
		outer.appendChild(inner);

		const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;

		outer.parentNode.removeChild(outer);

		return scrollbarWidth;
	}

	document.documentElement.style.setProperty(
		'--scrollbar-width',
		`${getScrollbarWidth()}px`,
	);
	window.addEventListener(
		'resize',
		debounce(() => {
			document.documentElement.style.setProperty(
				'--scrollbar-width',
				`${getScrollbarWidth()}px`,
			);
		}),
		100,
	);

	const pageWrapper = document.querySelector('.v-page-wrapper');

	window.addEventListener('vOverlayOpening', (e) => {
		pageWrapper.setAttribute('inert', '');
	});

	window.addEventListener('vOverlayClosed', () => {
		pageWrapper.toggleAttribute('inert', false);
	});

	/**
	 * Global Window Load.
	 *
	 * Runs polyfills on page load.
	 */
	async function onWindowLoad() {
		const body = document.querySelector('body');
		if (!body.classList.contains('admin-bar')) {
			const { listen } = await import('quicklink');

			listen({
				ignores: [
					(url) => {
						const cleanUrl = url.replace(/#.*/, '');
						return cleanUrl === window.location.href;
					},
					/.*\/wp-admin\/.*/,
				],
			});
		}
	}

	if (document.readyState === 'complete') {
		onWindowLoad();
	} else {
		window.addEventListener('load', onWindowLoad);
	}
};

if (document.readyState !== 'loading') {
	vGlobal();
} else {
	document.addEventListener('DOMContentLoaded', vGlobal);
}
